/**
 * The Sphere AI panel's content lane, shared by all three tabs.
 *
 * Full-screen gives the panel far more width than any of its content wants. The chat already
 * answered that with two nested bounds, and this puts History and Feedback on the same two, so
 * switching tabs doesn't move the content sideways:
 *
 *  - the OUTER bound is the panel's content width (`--thread-content-max-width`): the frame wide
 *    content is allowed to fill — charts and data tables in the chat, and anything equally wide a
 *    tab grows later. It is what stops full-screen content running the whole width of a monitor.
 *  - the INNER bound is the reading width (`--thread-max-width`): where text, rows and controls
 *    live. A row that stretched to the outer bound would put its ✕/★/⋮ controls a hand's width away
 *    from the label they act on, which is the whole reason the two bounds are not one.
 *
 * Both are centered, so one lane runs down the middle of the panel and wide content breaks out of
 * it symmetrically. See THREAD_READING_MAX_WIDTH for why the reading width comes from the thread
 * rather than being restated here.
 */
import { THREAD_READING_MAX_WIDTH } from "@mdotm/shadcn/assistant-ui";

/**
 * The panel's content width in full screen — the outer bound above, and the chat column's own width.
 * Wide enough for a chart or a data table to be worth widening for, narrow enough that the lane
 * inside it still reads as the middle of the panel rather than a margin.
 */
const PANEL_CONTENT_MAX_WIDTH = "80rem";

/**
 * Lane variables for the panel root, so every tab AND the thread nested inside it resolve the same
 * two bounds. Declared once at the top rather than per surface: they are inherited, and a tab that
 * set its own would be the one that drifts.
 *
 * `--thread-content-max-width` is only set in full screen. Left unset, the outer bound falls back to
 * the panel's own width, which is what docked and overlay want — there the panel is already narrower
 * than the reading width and both bounds are inert.
 */
export function panelLaneStyle(fullscreen: boolean): React.CSSProperties {
	return {
		"--thread-max-width": THREAD_READING_MAX_WIDTH,
		...(fullscreen && { "--thread-content-max-width": PANEL_CONTENT_MAX_WIDTH }),
	} as React.CSSProperties;
}

/**
 * Puts its children in the reading lane, inside the panel's content frame.
 *
 * Carries no padding of its own: each tab keeps its own gutters, which are tuned to land text at the
 * panel's 16px edge in the docked width (see the History list's inset), and a lane that added to them
 * would double that up.
 */
export function SphereAIPanelLane({ children }: React.PropsWithChildren): React.JSX.Element {
	return (
		<div className="mx-auto w-full max-w-[var(--thread-content-max-width,100%)]">
			<div className="mx-auto w-full max-w-[var(--thread-max-width,100%)]">{children}</div>
		</div>
	);
}
